import { createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { formatTextForSentryLog } from 'src/utils/Formatter';
import {
  deleteTrack,
  enterpriseUploadFilesToPlaylist,
  getArtists,
  getReleaseTracksByReleaseList,
  uploadFilesToPlaylist,
} from './MultiAsset.thunks';

interface S3File {
  bucket: string;
  key: string;
  filename: string;
  fileId?: string;
}

export interface ArtistResponse {
  id: string;
  name: string;
}

export interface Artist {
  code: string;
  isChecked: string;
  isDisabled: boolean;
  isDeleteAvailable?: boolean;
  name: string;
}

export interface ArtistItems {
  items: Artist[];
}

export interface AddTrackObj {
  track_id: string;
  isFileUploaded: boolean;
  isFileUploadError: boolean;
  isFetching: boolean;
  file?: File;
  artist: string;
  artists: Artist[];
  title: string;
  uploadS3Key: string;
  isSelected: string;
  isActive: boolean;
  S3File: S3File;
  isUpLoading: boolean;
  startTrackingDate?: Date;
  releaseTrack?: ReleaseTrack;
  release_date?: string;
  release_id?: string;
  release_title?: string;
}

export interface TrackId {
  track_id: string;
  artist: string;
  title: string;
}
interface ReleaseTrack {
  playlist_id: string;
  title: string;
  artist: string;
  artists: ArtistItems;
  album: string;
  version: string;
  language: string;
  genres: string[];
  trackid: number;
  status: string;
  explicit: boolean;
  is_monitored: boolean;
  release_id: string;
  release_date: string;
}

export interface Release {
  release_id?: string;
  release_date: string;
  isEmptyRelease: boolean;
  displayOnlyTracks: AddTrackObj[];
}

interface ReleaseTrackList {
  release_id: string;
  release_date: string;
  title: string;
  tracks: ReleaseTrack[];
}

export interface AddTrackList {
  tracks: AddTrackObj[];
  trackIds: TrackId[];
  releases_not_authorized?: string[];
  artists: ArtistItems;
  releaseTrack: Release;
  hasError: boolean;
  hasUnauthorizedError: boolean;
  isFetching: boolean;
  isEnterpriseTrackSuccessful: boolean;
}

const MultiAssetSlice = createSlice({
  name: 'AddTrack',
  initialState: {
    trackIds: [],
    tracks: [],
    artists: {} as ArtistItems,
    releaseTrack: {} as Release,
    hasError: false,
    hasUnauthorizedError: false,
    isFetching: false,
    isEnterpriseTrackSuccessful: false,
  } as AddTrackList,
  extraReducers: (builder) =>
    builder
      .addCase(uploadFilesToPlaylist.pending, (state, payload) => {
        state.isFetching = true;
        const uploadFiles = payload.meta.arg;
        const newTracks = uploadFiles.tracks.map((track) => {
          return {
            track_id: '',
            isFileUploaded: false,
            isFileUploadError: false,
            isFetching: false,
            file: undefined,
            artist: track.artist || '',
            artists: track.artists
              ? track.artists.map((a) => ({
                  code: a.id,
                  name: a.name,
                  isChecked: '',
                  isDisabled: false,
                  isDeleteAvailable: false,
                }))
              : [],
            title: track.title || '',
            uploadS3Key: '',
            isSelected: '',
            isActive: false,
            S3File: {
              bucket: track.upload_s3_bucket,
              key: track.upload_s3_key,
              filename: track.src_filename,
            },
            isUpLoading: true,
            startTrackingDate: undefined,
          };
        });
        state.tracks = newTracks;
      })
      .addCase(uploadFilesToPlaylist.fulfilled, (state, { payload }) => {
        state.trackIds = payload.data;
        state.isFetching = false;
        return state;
      })
      .addCase(uploadFilesToPlaylist.rejected, (state) => {
        state.hasError = true;
        state.isFetching = false;
        Sentry.captureException(
          formatTextForSentryLog('failed to upload file to play list')
        );
      })
      .addCase(enterpriseUploadFilesToPlaylist.pending, (state, payload) => {
        state.isFetching = true;
        state.hasError = false;
        const uploadFiles = payload.meta.arg;
        state.isEnterpriseTrackSuccessful = false;

        state.tracks = uploadFiles.items.map((track) => {
          return {
            track_id: '',
            isFileUploaded: false,
            isFileUploadError: false,
            isFetching: false,
            file: undefined,
            artist: track.artist || '',
            artists: [],
            title: track.title || '',
            uploadS3Key: '',
            isSelected: '',
            isActive: false,
            S3File: {
              bucket: track.upload_s3_bucket,
              key: track.upload_s3_key,
              filename: track.src_filename,
            },
            isUpLoading: true,
            startTrackingDate: new Date(track.start_date) || undefined,
          };
        });
      })
      .addCase(enterpriseUploadFilesToPlaylist.fulfilled, (state) => {
        state.trackIds = [{ track_id: '', artist: '', title: '' }];
        state.hasError = false;
        state.isFetching = false;
        state.isEnterpriseTrackSuccessful = true;
        return state;
      })
      .addCase(enterpriseUploadFilesToPlaylist.rejected, (state) => {
        state.hasError = true;
        state.isFetching = false;
        state.isEnterpriseTrackSuccessful = false;
        Sentry.captureException(
          formatTextForSentryLog('failed to upload file to play list')
        );
      })
      .addCase(deleteTrack.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(deleteTrack.fulfilled, (state, payload) => {
        state.tracks = state.tracks.filter(
          (track) => track.track_id !== payload.meta.arg[0]
        );
        state.isFetching = false;
        return state;
      })
      .addCase(deleteTrack.rejected, (state) => {
        state.hasError = true;
        state.isFetching = false;
        Sentry.captureException(
          formatTextForSentryLog('failed to delete track')
        );
      })
      .addCase(getReleaseTracksByReleaseList.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getReleaseTracksByReleaseList.fulfilled,
        (state, { payload }) => {
          state.isFetching = false;
          state.releaseTrack.release_id =
            payload.data.releases.length > 0 ||
            payload.data.releases_not_authorized.length > 0
              ? 'true'
              : undefined;

          state.releases_not_authorized = payload.data.releases_not_authorized;

          // add tracks and releases to state releaseTrackList
          state.tracks = payload.data.releases.flatMap(
            (release: ReleaseTrackList) =>
              release.tracks
                .filter((track: ReleaseTrack) => !track.is_monitored)
                .map((track: ReleaseTrack) => ({
                  track_id: track.trackid,
                  isFileUploaded: false,
                  isFileUploadError: false,
                  isFetching: false,
                  file: undefined,
                  artist: track.artist,
                  artists: [],
                  title: track.title,
                  uploadS3Key: '',
                  isSelected: '',
                  isActive: false,
                  S3File: undefined,
                  isUpLoading: false,
                  startTrackingDate: undefined,
                  release_id: release.release_id,
                  releaseTrack: track,
                  release_date: track.release_date,
                  release_title: release.title,
                }))
          );

          state.releaseTrack.isEmptyRelease = state.tracks.length === 0;

          state.releaseTrack.displayOnlyTracks = payload.data.releases.flatMap(
            (release: ReleaseTrackList) =>
              release.tracks
                .filter((track: ReleaseTrack) => track.is_monitored)
                .map((track: ReleaseTrack) => ({
                  track_id: track.trackid,
                  isFileUploaded: false,
                  isFileUploadError: false,
                  isFetching: false,
                  file: undefined,
                  artist: track.artist,
                  artists: [],
                  title: track.title,
                  uploadS3Key: '',
                  isSelected: '',
                  isActive: false,
                  S3File: undefined,
                  isUpLoading: false,
                  startTrackingDate: undefined,
                  release_id: release.release_id,
                  releaseTrack: track,
                  release_date: track.release_date,
                  release_title: release.title,
                }))
          );

          return state;
        }
      )
      .addCase(getReleaseTracksByReleaseList.rejected, (state, action) => {
        state.hasError = true;
        state.isFetching = false;
        const statusCode = action.payload;
        if (statusCode === 403) {
          state.hasUnauthorizedError = true;
        }
        Sentry.captureException(
          formatTextForSentryLog('failed to get release tracks')
        );
      })
      .addCase(getArtists.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getArtists.fulfilled, (state, { payload }) => {
        const seen = new Set<string>();
        state.artists.items = payload.data.promoted_artists
          .filter((artist: ArtistResponse) => {
            const trimmedName = artist.name.trim();
            if (seen.has(trimmedName)) return false;
            seen.add(trimmedName);
            return true;
          })
          .map((artist: ArtistResponse) => ({
            name: artist.name,
            code: artist.id,
            isChecked: '',
            isDisabled: false,
            isDeleteAvailable: false,
          }));
        state.isFetching = false;
        return state;
      })
      .addCase(getArtists.rejected, (state) => {
        state.hasError = true;
        state.isFetching = false;
        Sentry.captureException(
          formatTextForSentryLog('failed to get Artist list')
        );
      }),
  reducers: {
    resetStateAddTrack: () => {
      return {
        trackIds: [],
        tracks: [],
        artists: {} as ArtistItems,
        releaseTrack: {} as Release,
        hasError: false,
        hasUnauthorizedError: false,
        isFetching: false,
        isEnterpriseTrackSuccessful: false,
      } as AddTrackList;
    },
    updateTracks: (state, action) => {
      const { trackIds, tracks } = action.payload;

      if (trackIds) {
        state.tracks = tracks.map((track: AddTrackObj, index: number) => ({
          ...track,
          track_id: trackIds[index]?.track_id || '',
        }));
        state.trackIds = [];
      } else {
        state.tracks = tracks.map((track: AddTrackObj) => ({
          ...track,
        }));
      }

      return state;
    },
    updateArtists: (state, action) => {
      const artists = action.payload;
      if (artists) {
        state.artists.items = artists;
      }

      return state;
    },
    cleanReleasesNotAuthorized: (state) => {
      state.releases_not_authorized = [];
      return state;
    },
  },
});

export const {
  resetStateAddTrack,
  updateTracks,
  updateArtists,
  cleanReleasesNotAuthorized,
} = MultiAssetSlice.actions;

export default MultiAssetSlice.reducer;
